import { useEffect, useRef, useState } from 'react'
import { cmsApi } from './api'
import { formatWhen } from './format'
import type { CatalogDraft, PlaceForm, ProfileForm } from '../types'

type Content = PlaceForm | ProfileForm

// A new place's or profile's draft (PRD.md, item 98), as a new event's (items
// 11 and 73): saved two seconds after each change once something is filled,
// one save after another, on leaving and by the button; closed when the place
// or profile is created or the draft is deleted.
export function useCatalogDraft<T extends Content>({ kind, draftId, value, filled, enabled, store, onStored }: {
  kind: CatalogDraft['kind']
  draftId: string | null
  value: T | null
  filled: boolean
  enabled: boolean
  // The form with its photo in the Guve, so the draft opens on another day.
  store: (value: T) => Promise<T>
  onStored: (value: T) => void
}) {
  const id = useRef<string | null>(draftId)
  const saved = useRef('')
  const queue = useRef<Promise<boolean> | null>(null)
  const closed = useRef(false)
  const pending = useRef<(() => void) | null>(null)
  const [state, setState] = useState<{ status: 'idle' | 'saving' | 'saved' | 'error'; at?: string }>({ status: 'idle' })

  // What the draft holds as it opens, so opening saves nothing.
  const opened = (content: T, at?: string) => {
    saved.current = JSON.stringify(content)
    if (at) setState({ status: 'saved', at })
  }

  const save = (content: T, force = false): Promise<boolean> => {
    const run = async () => {
      if (closed.current) return true
      if (!force && JSON.stringify(content) === saved.current) return true
      setState({ status: 'saving' })
      const kept = await store(content)
      if (kept !== content) onStored(kept)
      id.current ??= crypto.randomUUID()
      const result = await cmsApi.saveCatalogDraft(id.current, kind, kept)
      saved.current = JSON.stringify(kept)
      setState({ status: 'saved', at: result.updated_at })
      return true
    }
    const next = (queue.current ?? Promise.resolve(true)).then(run).catch((error) => {
      if (!closed.current) setState({ status: 'error' })
      if (force) throw error
      return false
    })
    queue.current = next
    return next
  }

  useEffect(() => {
    if (!enabled || !value || !filled) return
    const flush = () => { pending.current = null; void save(value) }
    pending.current = flush
    const timer = window.setTimeout(flush, 2000)
    return () => window.clearTimeout(timer)
  }, [enabled, value, filled])
  // Leaving the editor saves what the last change left waiting.
  useEffect(() => () => pending.current?.(), [])

  return {
    id,
    state,
    opened,
    // Whether the form has changes the draft doesn't have yet.
    behind: (content: T) => state.status === 'saving' || state.status === 'error' || JSON.stringify(content) !== saved.current,
    label: state.status === 'saving' ? 'Salvando rascunho…'
      : state.status === 'error' ? 'Rascunho não salvo'
        : state.status === 'saved' && state.at ? `Rascunho salvo · ${formatWhen(state.at)}`
          : 'Alterações não salvas',
    saveNow: (content: T) => save(content, true),
    // The save under way finishes first; none follows. Undone when what
    // follows fails.
    close: async () => { closed.current = true; pending.current = null; await queue.current },
    reopen: () => { closed.current = false },
    discard: async () => {
      closed.current = true
      pending.current = null
      await queue.current
      if (id.current) await cmsApi.discardCatalogDraft(id.current)
    },
  }
}
